import React, { useEffect, useMemo, useState } from 'react';
import {
  getSessionsForUser,
  getCompletionsForUser,
  buildQuizPlayerUrl,
} from '../utils/quizSessionManager';
import {
  FileText,
  CheckCircle2,
  Clock,
  Award,
  BarChart3,
  CalendarClock,
  Play,
  Footprints,
  Star,
  Crown,
  Flame,
  Trophy,
  Lock,
  X,
} from 'lucide-react';
import DashboardBookings from './DashboardBookings';
import './TrainingDashboard.css';

const pct = (score, total) => (total > 0 ? Math.round(((score || 0) / total) * 100) : 0);

// DEV/DEMO ONLY: sample data shown when the URL has ?demo=1, so you can preview
// how the consultant dashboard looks once quizzes are assigned. Never affects real data.
const isDemoMode = () => {
  try {
    const param = new URLSearchParams(window.location.search).get('demo');
    if (param === '1') window.sessionStorage.setItem('demoMode', '1');
    else if (param === '0' || param === 'off') window.sessionStorage.removeItem('demoMode');
    return window.sessionStorage.getItem('demoMode') === '1';
  } catch {
    return false;
  }
};

const DEMO_PENDING = [
  { id: 'demo-p1', title: 'ICD-10 Coding', createdAt: new Date(), quizFileUrl: '' },
  { id: 'demo-p2', title: 'Medical Procedures', createdAt: new Date(Date.now() - 2 * 86400000), quizFileUrl: '' },
  { id: 'demo-p3', title: 'Compliance Basics', createdAt: new Date(Date.now() - 5 * 86400000), quizFileUrl: '' },
];

const DEMO_COMPLETIONS = [
  { id: 'demo-c1', sessionId: 'demo-s1', sessionTitle: 'HIPAA Fundamentals', score: 9, totalQuestions: 10, completedAt: new Date(Date.now() - 7 * 86400000) },
  { id: 'demo-c2', sessionId: 'demo-s2', sessionTitle: 'Medical Terminology', score: 8, totalQuestions: 10, completedAt: new Date(Date.now() - 12 * 86400000) },
];

// Badge catalog — each defines how it's earned from the user's completions/progress.
const BADGE_CATALOG = [
  {
    id: 'first-steps',
    name: 'First Steps',
    desc: 'Complete your first quiz',
    Icon: Footprints,
    earned: (s) => s.quizzesTaken >= 1,
  },
  {
    id: 'high-achiever',
    name: 'High Achiever',
    desc: 'Score 90% or more on any quiz',
    Icon: Star,
    earned: (s) => s.bestScore >= 90,
  },
  {
    id: 'perfectionist',
    name: 'Perfectionist',
    desc: 'Score a perfect 100%',
    Icon: Crown,
    earned: (s) => s.bestScore >= 100,
  },
  {
    id: 'consistent',
    name: 'Consistent',
    desc: 'Pass 3 quizzes (70%+)',
    Icon: Flame,
    earned: (s) => s.passedCount >= 3,
  },
  {
    id: 'all-rounder',
    name: 'All Rounder',
    desc: 'Complete every assigned module',
    Icon: CheckCircle2,
    earned: (s) => s.completedModules > 0 && s.pendingCount === 0,
  },
  {
    id: 'quiz-master',
    name: 'Quiz Master',
    desc: 'Complete 10 quizzes',
    Icon: Trophy,
    earned: (s) => s.quizzesTaken >= 10,
  },
];

const fmtDate = (ts) => {
  const d = ts?.toDate?.() || (ts instanceof Date ? ts : null);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const TrainingDashboard = ({ user }) => {
  const email = (user?.email || '').toLowerCase();
  const [loading, setLoading] = useState(true);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showEarnedModal, setShowEarnedModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (isDemoMode()) {
      setPendingSessions(DEMO_PENDING);
      setCompletions(DEMO_COMPLETIONS);
      setLoading(false);
      return;
    }
    if (!email) return;
    setLoading(true);
    (async () => {
      try {
        const [sessions, comps] = await Promise.all([
          getSessionsForUser(email),
          getCompletionsForUser(email),
        ]);
        if (cancelled) return;
        setPendingSessions(sessions);
        setCompletions(comps);
      } catch (err) {
        console.error('TrainingDashboard load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const stats = useMemo(() => {
    const completedSessionIds = new Set(
      completions.map((c) => c.sessionId).filter(Boolean)
    );
    const completedModules = completedSessionIds.size || completions.length;
    const totalModules = pendingSessions.length + completedModules;
    const progress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

    const percentages = completions.map((c) => pct(c.score, c.totalQuestions));
    const avgScore = percentages.length
      ? Math.round(percentages.reduce((s, p) => s + p, 0) / percentages.length)
      : 0;
    const bestScore = percentages.length ? Math.max(...percentages) : 0;
    const passedCount = percentages.filter((p) => p >= 70).length;

    return {
      completedModules,
      totalModules,
      progress,
      avgScore,
      bestScore,
      passedCount,
      quizzesTaken: completions.length,
      pendingCount: pendingSessions.length,
    };
  }, [completions, pendingSessions]);

  const badgeList = useMemo(
    () => BADGE_CATALOG.map((b) => ({ ...b, isEarned: b.earned(stats) })),
    [stats]
  );
  const earnedBadges = useMemo(() => badgeList.filter((b) => b.isEarned), [badgeList]);
  const earnedBadgeCount = earnedBadges.length;

  // Module list: completed (with score) + pending (continue)
  const modules = useMemo(() => {
    const completedRows = completions.map((c) => {
      const date = fmtDate(c.completedAt);
      return {
        key: `c-${c.id}`,
        title: c.sessionTitle || 'Quiz',
        status: 'completed',
        score: pct(c.score, c.totalQuestions),
        caption: date ? `Completed · ${date}` : 'Completed',
      };
    });
    const pendingRows = pendingSessions.map((s) => {
      const date = fmtDate(s.createdAt);
      return {
        key: `p-${s.id}`,
        title: s.title || s.quizFileName || 'Quiz',
        status: 'continue',
        session: s,
        caption: date ? `Assigned · ${date}` : 'Assigned',
      };
    });
    return [...pendingRows, ...completedRows];
  }, [completions, pendingSessions]);

  const startQuiz = (session) => {
    if (!session?.quizFileUrl) return;
    window.location.href = buildQuizPlayerUrl({
      quizFileUrl: session.quizFileUrl,
      sessionId: session.id,
      durationMinutes: session.durationMinutes,
    });
  };

  if (loading) {
    return (
      <div className="td-loading">
        <div className="loading-spinner" />
        <p>Loading your training dashboard...</p>
      </div>
    );
  }

  return (
    <div className="training-dashboard">
      <div className="td-main">
        {/* Total progress */}
        <div className="td-card td-progress-card">
          <div className="td-progress-head">
            <h3>Total Quiz Progress</h3>
            <span className="td-progress-count">
              {stats.completedModules}/{stats.totalModules} Modules Completed
            </span>
          </div>
          <div className="td-progress-track">
            <div className="td-progress-bar" style={{ width: `${stats.progress}%` }} />
          </div>
        </div>

        {/* Key stats */}
        <div className="td-card td-keystats-card">
          <h3 className="td-section-title">Key Stats</h3>
          <div className="td-keystats-grid">
            <div className="td-stat">
              <div className="td-stat-icon"><BarChart3 size={18} /></div>
              <div className="td-stat-value">{stats.avgScore}%</div>
              <div className="td-stat-label">Average Score</div>
            </div>
            <div className="td-stat">
              <div className="td-stat-icon"><CheckCircle2 size={18} /></div>
              <div className="td-stat-value">{stats.quizzesTaken}</div>
              <div className="td-stat-label">Quizzes Taken</div>
            </div>
            <button
              type="button"
              className={`td-stat td-stat-clickable td-stat-badges ${earnedBadgeCount > 0 ? 'has-badges' : ''}`}
              onClick={() => setShowEarnedModal(true)}
            >
              <div className="td-stat-icon td-badge-stat-icon"><Award size={18} /></div>
              <div className="td-stat-value">{earnedBadgeCount}</div>
              <div className="td-stat-label">Badges Earned</div>
            </button>
          </div>
        </div>

        {/* Module quiz list */}
        <div className="td-card td-modules-card">
          <h3 className="td-section-title">Module Quiz List</h3>
          {modules.length === 0 ? (
            <p className="td-empty">No quizzes assigned yet.</p>
          ) : (
            <div className="td-module-list">
              {modules.map((m) => (
                <div key={m.key} className={`td-module-row td-module-${m.status}`}>
                  <div className="td-module-icon">
                    {m.status === 'completed'
                      ? <CheckCircle2 size={18} />
                      : <FileText size={18} />}
                  </div>
                  <div className="td-module-text">
                    <div className="td-module-title">{m.title}</div>
                    {m.caption && <div className="td-module-caption">{m.caption}</div>}
                  </div>
                  {m.status === 'continue' ? (
                    <button
                      type="button"
                      className="td-module-btn continue"
                      onClick={() => startQuiz(m.session)}
                    >
                      <Play size={13} /> Start
                    </button>
                  ) : (
                    <span className="td-module-btn done">{m.score}%</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right sidebar */}
      <div className="td-side">
        <div className="td-card td-bookings-card">
          <DashboardBookings user={user} variant="consultant" />
        </div>
        <div className="td-card td-deadlines-card">
          <h3 className="td-section-title"><CalendarClock size={16} /> Upcoming Deadlines</h3>
          {pendingSessions.length === 0 ? (
            <p className="td-empty">You're all caught up — no pending assignments.</p>
          ) : (
            <ul className="td-deadlines">
              {pendingSessions.slice(0, 5).map((s) => (
                <li key={s.id} className="td-deadline-row">
                  <div className="td-deadline-icon"><Clock size={16} /></div>
                  <div className="td-deadline-info">
                    <div className="td-deadline-title">{s.title || s.quizFileName || 'Quiz'}</div>
                    <div className="td-deadline-meta">New assignment · Pending</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showEarnedModal && (
        <div className="td-badge-modal-overlay" onClick={() => setShowEarnedModal(false)}>
          <div
            className="td-badge-modal td-earned-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="td-badge-modal-close"
              onClick={() => setShowEarnedModal(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="td-earned-modal-head">
              <div className="td-earned-modal-trophy">
                <span className="td-trophy-ring" aria-hidden="true" />
                <Award size={26} />
              </div>
              <h3 className="td-earned-modal-title">Badges Earned</h3>
              <p className="td-earned-modal-sub">
                You've earned {earnedBadgeCount} of {badgeList.length} badges
              </p>
            </div>
            {earnedBadges.length === 0 ? (
              <div className="td-earned-empty">
                <Lock size={28} />
                <p>No badges yet. Complete a quiz to earn your first badge!</p>
              </div>
            ) : (
              <div className="td-earned-grid">
                {earnedBadges.map((b, i) => {
                  const BIcon = b.Icon;
                  return (
                    <button
                      type="button"
                      key={b.id}
                      className="td-earned-item"
                      style={{ '--badge-i': i }}
                      onClick={() => { setShowEarnedModal(false); setSelectedBadge(b); }}
                    >
                      <div className="td-earned-item-icon">
                        <span className="td-badge-shine" aria-hidden="true" />
                        <BIcon size={22} />
                      </div>
                      <div className="td-earned-item-name">{b.name}</div>
                      <div className="td-earned-item-desc">{b.desc}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedBadge && (
        <div className="td-badge-modal-overlay" onClick={() => setSelectedBadge(null)}>
          <div
            className={`td-badge-modal ${selectedBadge.isEarned ? 'earned' : 'locked'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="td-badge-modal-close"
              onClick={() => setSelectedBadge(null)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className={`td-badge-modal-icon ${selectedBadge.isEarned ? 'is-earned' : 'is-locked'}`}>
              {selectedBadge.isEarned && (
                <>
                  <span className="td-badge-burst" aria-hidden="true" />
                  <span className="td-spark s1" aria-hidden="true" />
                  <span className="td-spark s2" aria-hidden="true" />
                  <span className="td-spark s3" aria-hidden="true" />
                  <span className="td-spark s4" aria-hidden="true" />
                </>
              )}
              {selectedBadge.isEarned
                ? <selectedBadge.Icon size={40} />
                : <Lock size={32} />}
            </div>
            <h3 className="td-badge-modal-name">{selectedBadge.name}</h3>
            <span className={`td-badge-modal-status ${selectedBadge.isEarned ? 'earned' : 'locked'}`}>
              {selectedBadge.isEarned ? 'Earned' : 'Locked'}
            </span>
            <p className="td-badge-modal-desc">
              {selectedBadge.isEarned
                ? 'Congratulations! You unlocked this badge.'
                : 'Keep going to unlock this badge.'}
            </p>
            <div className="td-badge-modal-howto">
              <span className="td-badge-modal-howto-label">How to earn</span>
              <span className="td-badge-modal-howto-text">{selectedBadge.desc}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDashboard;
